import { DropdownMenu, Button, DropdownItem } from '@dev-spendesk/grapes';
import React, { useEffect, useState } from 'react';
import { useTimeout } from 'react-use';

import { useTranslation } from 'common/hooks/useTranslation';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications';
import { AnalyticEventName, track } from 'src/core/utils/analytics';
import { exportAsCsv } from 'src/core/utils/exportAsCsv';
import { logger } from 'src/utils/datadog-log-wrapper';

import { columns, CSV_SEPARATOR, type RowData } from './template';
import { useGetEntityActions } from '../../../hooks/data/useGetEntityActions';
import { useOrganisationEntityWalletSummaryQuery } from '../../../hooks/data/useOrganisationEntityWalletSummaryQuery';
import { useOrganisationFeatures } from '../../../hooks/useOrganisationFeatures';
import {
  type EntityActions,
  type EntityDetailedInformation,
  type EnrichedEntityInformation,
  type EntityBasicInformation,
} from '../../../types';
import { getEntityStatus } from '../entity-list-item/EntityListItem';

type Entity = EntityBasicInformation | EnrichedEntityInformation;
type Props = {
  entities: Entity[];
};

type EnrichedExportRowData = RowData & { id: string; ready: boolean };

const LoadEntityData = ({
  entity,
  entityExportData,
  setEntityExportData,
  setDataFetchingFailed,
}: {
  entity: Entity;
  entityExportData: EnrichedExportRowData;
  setEntityExportData: (data: EnrichedExportRowData) => void;
  setDataFetchingFailed: () => void;
}) => {
  const features = useOrganisationFeatures();
  const organisationEntityWalletSummary =
    useOrganisationEntityWalletSummaryQuery(entity.id);
  const entityActionsQueryState = useGetEntityActions(entity, features);
  const [isTimeoutDone, cancelTimeout] = useTimeout(1000);

  useEffect(() => {
    if (isTimeoutDone() === true) {
      setDataFetchingFailed();
      return () => {};
    }

    if (
      organisationEntityWalletSummary.status === 'success' &&
      entityActionsQueryState.status === 'success'
    ) {
      const wallet = organisationEntityWalletSummary.data;
      const actions = entityActionsQueryState.data;

      setEntityExportData({
        ...entityExportData,
        ...mapEnrichedExportRowData({
          entity,
          walletSummary: wallet,
          actions,
          ready: false,
        }),
      });
    }
    if (
      organisationEntityWalletSummary.status === 'error' ||
      entityActionsQueryState.status === 'error'
    ) {
      setDataFetchingFailed();
    }

    return () => {
      cancelTimeout();
    };
  }, [
    organisationEntityWalletSummary.status,
    entityActionsQueryState.status,
    isTimeoutDone() !== false,
  ]);

  return null;
};

const LoadEntitiesData = ({
  entities,
  exportData,
  setEntityExportData,
  children,
  isReady,
}: {
  entities: Entity[];
  exportData: EnrichedExportRowData[];
  setEntityExportData: (entity: Entity, data: EnrichedExportRowData) => void;
  isReady: boolean;
  children: (isReady: boolean, hasError: boolean) => React.ReactNode;
}) => {
  const [hasError, setHasError] = useState(false);

  return (
    <>
      {entities.map((entity) => (
        <LoadEntityData
          key={entity.id}
          entity={entity}
          entityExportData={
            exportData.find(
              (item) => item.id === entity.id,
            ) as EnrichedExportRowData
          }
          setEntityExportData={(data) => setEntityExportData(entity, data)}
          setDataFetchingFailed={() => setHasError(true)}
        />
      ))}
      {children(isReady, hasError)}
    </>
  );
};

export const EntityListExport = ({ entities }: Props) => {
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();

  const [orgExportData, updateOrgExportData] = useState<
    EnrichedExportRowData[]
  >(
    entities.map((entity) =>
      mapEnrichedExportRowData({ entity, ready: false }),
    ),
  );
  useEffect(() => {
    updateOrgExportData((currentExportData) =>
      entities.map((entity) => {
        return (
          currentExportData.find((item) => item.id === entity.id) ??
          mapEnrichedExportRowData({ entity, ready: false })
        );
      }),
    );
  }, [entities]);

  const triggerExport = async () => {
    track(AnalyticEventName.ORGANISATION_REPORTING_EXPORT_BUTTON_CLICKED, {
      entities: orgExportData.map((entity) => entity.id),
    });

    try {
      exportAsCsv({
        items: orgExportData,
        columns,
        translator: t,
        csvSeparator: CSV_SEPARATOR,
        fileName: `entity-list-export_${getFormattedExportDate()}.csv`,
      });
    } catch (error) {
      logger.warn('Failed to export entity list', {
        scope: 'multi-entity-hub',
        team: 'control',
        error,
        entities,
      });
      pushNotif({
        type: NotificationType.Danger,
        message: t('organisation.reporting.export.errorNotification'),
      });
    }
  };

  return (
    <LoadEntitiesData
      entities={entities}
      exportData={orgExportData}
      isReady={orgExportData.every((entity) => entity.ready)}
      setEntityExportData={(
        currentEntity: Entity,
        data: EnrichedExportRowData,
      ) => {
        updateOrgExportData((currentOrgExportData) =>
          currentOrgExportData.map((entity) =>
            entity.id === currentEntity.id
              ? { ...entity, ...data, ready: true }
              : entity,
          ),
        );
      }}
    >
      {(isReady, hasError) =>
        hasError ? null : (
          <DropdownMenu
            options={[
              {
                key: 'summary',
                label: t(
                  'organisation.reporting.export.dropdown.summary.label',
                ),
                fileType: t(
                  'organisation.reporting.export.dropdown.summary.fileType',
                ),
                action: triggerExport,
              },
            ]}
            renderButton={(getToggleButtonProps) => (
              <Button
                {...getToggleButtonProps()}
                isLoading={!isReady}
                variant="secondaryNeutral"
                text={t('organisation.reporting.export.dropdown.label')}
              />
            )}
            renderOption={(option) => (
              <DropdownItem
                className="min-w-[240px]"
                label={option.label}
                suffix={option.fileType}
              />
            )}
            onSelect={(option) => option.action()}
          />
        )
      }
    </LoadEntitiesData>
  );
};

const mapEnrichedExportRowData = (data: {
  entity: EntityBasicInformation;
  ready: boolean;
  walletSummary?: EntityDetailedInformation;
  actions?: EntityActions;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}): EnrichedExportRowData => {
  const entityStatus =
    data.walletSummary &&
    getEntityStatus({ ...data.entity, ...data.walletSummary })
      ? 'inactive'
      : 'active';
  const fundsStatus =
    (data.walletSummary?.breakdown?.shortfall ?? 0) === 0
      ? 'available'
      : 'shortfall';

  return {
    id: data.entity.id,
    entityName: data.entity.name,
    entityCountry: data.entity.country,
    entityCurrency: data.entity.currency,
    entityStatus,
    fundsStatus,
    balance: data.walletSummary?.walletBalance ?? 0,
    scheduled: data.walletSummary?.breakdown.scheduledTransfers ?? 0,
    cards: data.walletSummary?.breakdown.cardsLimits ?? 0,
    available: data.walletSummary?.breakdown.available ?? 0,
    inboxInvoicesToAssign: data.actions?.inboxInvoices.toAssign ?? 0,
    inboxInvoicesAwaiting: data.actions?.inboxInvoices.awaiting ?? 0,
    requestsAll: data.actions?.requests.all ?? 0,
    requestsToApprove: data.actions?.requests.toApprove ?? 0,
    invoicesToReview: data.actions?.invoices.toReview ?? 0,
    invoicesToSchedule: data.actions?.invoices.toSchedule ?? 0,
    invoicesToConfirm: data.actions?.invoices.toConfirm ?? 0,
    expenseClaimsToReview: data.actions?.expenseClaims.toReview ?? 0,
    expenseClaimsToReimburse: data.actions?.expenseClaims.toReimburse ?? 0,
    expenseClaimsToConfirm: data.actions?.expenseClaims.toConfirm ?? 0,
    receiptsLate: data.actions?.receipts.late ?? 0,
    receiptsMissing: data.actions?.receipts.missing ?? 0,
    payablesToPrepare: data.actions?.payables.toPrepare ?? 0,
    payablesToExport: data.actions?.payables.toExport ?? 0,
    ready: data.ready,
  };
};

const getFormattedExportDate = () => {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}${month}${date.getDate()}`;
};
